import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, bearerAuth, getClient, RootState } from '@src/core/store';
import { SaleChange } from '@src/core/buyerApiClient';

export interface SaleChangeState {
  currentSaleChange?: SaleChange;
  loading: boolean;
  loadingCurrentSaleChange: boolean;
  showAppliedFeedbackForSaleChangeId: string | null;
}

const initialState: SaleChangeState = {
  currentSaleChange: undefined,
  loading: false,
  loadingCurrentSaleChange: false,
  showAppliedFeedbackForSaleChangeId: null,
};

export const saleChangeSlice = createSlice({
  name: 'saleChange',
  initialState,
  reducers: {
    setCurrentSaleChange: (state: SaleChangeState, action: PayloadAction<SaleChange>) => {
      state.currentSaleChange = action.payload;
    },
    setProcessedSaleChange: (state: SaleChangeState, action: PayloadAction<SaleChange>) => {
      state.currentSaleChange = action.payload;
    },
    setSaleChangeLoading: (state: SaleChangeState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingCurrentSaleChange: (state: SaleChangeState, action: PayloadAction<boolean>) => {
      state.loadingCurrentSaleChange = action.payload;
    },
    resetSaleChange: (state: SaleChangeState) => {
      state.currentSaleChange = undefined;
      state.loading = false;
      state.loadingCurrentSaleChange = false;
    },
    setShowAppliedFeedbackForSaleChange: (state: SaleChangeState, action: PayloadAction<string | null>) => {
      state.showAppliedFeedbackForSaleChangeId = action.payload;
    },
  },
});

export const {
  setCurrentSaleChange,
  setProcessedSaleChange,
  setSaleChangeLoading,
  setLoadingCurrentSaleChange,
  resetSaleChange,
  setShowAppliedFeedbackForSaleChange,
} = saleChangeSlice.actions;

export const loadSaleChangeByIdAsCurrent =
  (saleChangeId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoadingCurrentSaleChange(true));
    try {
      const state = getState();

      const {
        auth: { buyerToken },
      } = state;

      if (buyerToken == null) {
        return;
      }

      const { data } = await getClient(state).saleChanges.getSaleChange(saleChangeId, bearerAuth(buyerToken));
      dispatch(setCurrentSaleChange(data));
    } finally {
      dispatch(setLoadingCurrentSaleChange(false));
    }
  };

export const currentSaleChangeSelector = (state: RootState) => state.saleChange.currentSaleChange;

export const loadingCurrentSaleChangeSelector = (state: RootState) => state.saleChange.loadingCurrentSaleChange;

export const showAppliedFeedbackForSaleChangeIdSelector = (state: RootState) =>
  state.saleChange.showAppliedFeedbackForSaleChangeId;

export default saleChangeSlice.reducer;
